import * as queries from "../../graphql/queries";
import * as mutations from "../../graphql/mutations";
import * as customQueries from "../../graphql/customQueries";
import {
  ListUsersQuery,
  CreateUserMutation,
  UpdateUserMutation,
  User,
  DeleteUserInput,
  DeleteUserMutation,
  ModelUserConnection,
  UsersByNPIQuery,
  SearchUsersQuery,
  SearchableUserConnection,
  SearchUsersQueryVariables,
  ProgramMembership,
  ModelProgramMembershipConnection,
  UserStatsQuery,
  UserStats,
  UpdateUserEmailArgs,
  UpdateUserEmailQuery,
  ProgramRole,
  SearchProgramMembershipsQuery,
  SearchProgramMembershipsQueryVariables,
  SendUserInvitationResult,
  SendUserInvitationQuery,
  PaginatableSiblingUsersArgs,
  PaginatableSiblingUsersQuery,
  PaginatableSiblingUsersResult,
  PaginatableSiblingUsersQueryVariables,
  SendUserInvitationQueryVariables,
  SendUserInvitationArgs,
  AssessmentRole
} from "../../API";
import { ProgramUser } from "../../custom-types";
import {
  genericQuery,
  listQueryAutoPaginated,
  searchQuery
} from "../api/apiUtils";
import { API } from "aws-amplify";
import {
  simplApiRequest,
  SimplApiHttpError
} from "../../lib/simpl-api-client";
import { transformUser } from "../../lib/response-transformers";

/**
 * Fetch a single user by id or email via the REST endpoint DIRECTLY (no GraphQL
 * op indirection). Reuses transformUser so the returned shape is identical to
 * the old `genericQuery(queries.getUser, "getUser", ...)` path; replicates the
 * GetUser mapping's id-or-email URL and its 404 -> null (handleNotFound).
 */
async function fetchUserRest(predicate: {
  id?: string;
  email?: string;
  emailLower?: string;
  lowerEmail?: string;
}): Promise<User | null> {
  const email =
    predicate?.email ||
    predicate?.emailLower ||
    predicate?.lowerEmail ||
    undefined;
  const path = email
    ? `/api/user?email=${encodeURIComponent(String(email))}`
    : `/api/user?userId=${encodeURIComponent(String(predicate?.id ?? ""))}`;
  try {
    const restData = await simplApiRequest<any>("GET", path);
    return (transformUser(restData)?.data?.getUser ?? null) as User | null;
  } catch (e) {
    if (e instanceof SimplApiHttpError && e.status === 404) return null;
    throw e;
  }
}

/**
 * Fetch all the user objects. Optionally pass in a predicate for filtering, etc.
 * @param predicate The object to use as a predicate (ex: {filter: {email: {eq: "test@fake.com"}}})
 * @returns An array of User objects or null
 */
export const fetchAllUsers = async (predicate?: object) => {
  return await listQueryAutoPaginated<
    ListUsersQuery,
    User,
    ModelUserConnection
  >(queries.listUsers, "listUsers", "items", predicate);
};

/**
 * Fetch all users with the email addressed passed in.
 * @param email The email address to return matching users for.
 * @returns  An array of User objects or null
 */
export const fetchUsersByEmail = async (
  email: string
): Promise<User[] | null> => {
  // Exact lookup via GET /api/user?email=... (direct REST, no GraphQL op).
  const userByEmail = await fetchUserRest({ email });
  return userByEmail ? [userByEmail] : null;
};

export const fetchUsersByProgramIds = async (
  programIds: string[],
  role?: ProgramRole,
  activeUsersOnly = false
): Promise<ProgramUser[] | null> => {
  const filterClauses: NonNullable<
    SearchProgramMembershipsQueryVariables["filter"]
  >[] = [
    {
      or: programIds.map((pid) => {
        return { programId: { eq: pid } };
      })
    }
  ];

  if (role) {
    filterClauses.push({ programRole: { eq: role } });
  }

  if (activeUsersOnly) {
    filterClauses.push({
      or: [{ endDate: { exists: false } }, { endDate: { eq: "" } }]
    });
  }

  const variables: SearchProgramMembershipsQueryVariables = {
    filter:
      filterClauses.length === 1
        ? filterClauses[0]
        : { and: filterClauses }
  };

  const results = await listQueryAutoPaginated<
    SearchProgramMembershipsQuery,
    ProgramMembership,
    ModelProgramMembershipConnection
  >(
    queries.searchProgramMemberships,
    "searchProgramMemberships",
    "items",
    variables
  );

  const uniqueUsers: User[] = [];
  const finalResults: ProgramUser[] = [];

  if (results) {
    for (const membership of results) {
      const member = membership.programMember;
      const inUniqueUsers = uniqueUsers.find((u) => u.id === member?.id);
      if (member && !inUniqueUsers) {
        uniqueUsers.push(member);
      }
    }
    for (const user of uniqueUsers) {
      const programMemberships = results.filter(
        (r) => r.programMemberId === user.id
      );
      const roles = programMemberships.map(
        (membership) => membership.programRole
      );
      const programNames = programMemberships.map(
        (membership) => membership.program?.name
      );
      const institutionNames = programMemberships.map(
        (membership) => membership.program?.institution?.name
      );
      const entry: ProgramUser = {
        ...user,
        role: roles.join(",\r\n"),
        program: programNames.join(",\r\n"),
        institutionName: institutionNames.join(",\r\n"),
        status: user.eulaAcceptedDate ? "Active" : "Unregistered",
        programMemberships: {
          __typename: "ModelProgramMembershipConnection",
          items: programMemberships
        }
      };
      finalResults.push(entry);
    }
  }

  if (finalResults) {
    return finalResults;
  }
  return null;
};

/**
 * Fetch all users with the npi passed in.
 * @param npi The npi to return matching users for.
 * @returns An array of User objects or null
 */
export const fetchUsersByNPI = async (npi: number): Promise<User[] | null> => {
  const path = "users-by-npi";
  const resp = await API.get("simpl-api", path, {
    queryStringParameters: { npi }
  });
  const users = Array.isArray(resp) ? resp : resp?.users;
  if (!Array.isArray(users) || users.length === 0) {
    return null;
  }

  // Hydrate programMemberships: AddUserToProgram needs them to save. A failed
  // load throws rather than returning a user without them.
  const hydratedUsers = await Promise.all(
    users.map(async (user) => (await fetchUserRest({ id: user.id })) ?? user)
  );

  return hydratedUsers;
};

export const searchUsers = async (predicate: SearchUsersQueryVariables) => {
  return await searchQuery<SearchUsersQuery, SearchableUserConnection>(
    queries.searchUsers,
    "searchUsers",
    predicate
  );
};

// Direct REST call to the users-list Lambda — bypasses the GraphQL transformer
// layer. Returns the raw Lambda response shape (`{ users, pagination }`) so the
// caller can inspect every field without worrying about a transform stripping
// or renaming anything.
//
// `filter` and `sort` are forwarded as JSON-encoded query params per the Lambda's
// query-string interface; pass `nextToken` (offset string) for pagination.
export type UsersListDirectParams = {
  limit?: number;
  nextToken?: string;
  filter?: Record<string, any>;
  sort?: Array<{ field: string; direction: "asc" | "desc" }>;
  programId?: string;
  role?: string;
  status?: string;
};

export type UsersListDirectResponse = {
  users: any[];
  pagination: { nextOffset: string | null };
};

export const fetchUsersListDirect = async (
  params: UsersListDirectParams = {}
): Promise<UsersListDirectResponse> => {
  const queryStringParameters: Record<string, string> = {};
  if (params.limit != null) queryStringParameters.limit = String(params.limit);
  if (params.nextToken) queryStringParameters.nextToken = params.nextToken;
  if (params.filter) queryStringParameters.filter = JSON.stringify(params.filter);
  if (params.sort) queryStringParameters.sort = JSON.stringify(params.sort);
  if (params.programId) queryStringParameters.programId = params.programId;
  if (params.role) queryStringParameters.role = params.role;
  if (params.status) queryStringParameters.status = params.status;

  return (await API.get("simpl-api", "users", {
    queryStringParameters
  })) as UsersListDirectResponse;
};

// Direct REST call to the search-program-memberships Lambda — bypasses the
// GraphQL transformer layer. Mirrors fetchUsersByProgramIds's dedup-and-shape
// step (returns ProgramUser[] with programMemberships nested as { items: [...] }).
export const fetchUsersByProgramIdsDirect = async (
  programIds: string[],
  role?: ProgramRole,
  activeUsersOnly = false
): Promise<ProgramUser[] | null> => {
  if (programIds.length === 0) return null;

  const filterClauses: any[] = [
    {
      or: programIds.map((pid) => ({ programId: { eq: pid } }))
    }
  ];
  if (role) filterClauses.push({ programRole: { eq: role } });
  if (activeUsersOnly) {
    filterClauses.push({
      or: [{ endDate: { exists: false } }, { endDate: { eq: "" } }]
    });
  }
  const filter =
    filterClauses.length === 1 ? filterClauses[0] : { and: filterClauses };

  const resp = (await API.get("simpl-api", "program-memberships/search", {
    queryStringParameters: { filter: JSON.stringify(filter) }
  })) as { programmemberships?: any[] };

  const memberships = resp?.programmemberships ?? [];

  // Dedup users by id, then group their memberships under each user
  // in the same { items: [...] } shape the columns/consumers expect.
  const uniqueUsers: any[] = [];
  for (const m of memberships) {
    const member = m.programMember;
    if (member && !uniqueUsers.find((u) => u.id === member.id)) {
      uniqueUsers.push(member);
    }
  }

  const finalResults: ProgramUser[] = uniqueUsers.map((u) => {
    const usersMemberships = memberships.filter(
      (m) => m.programMemberId === u.id
    );
    const roles = usersMemberships.map((m) => m.programRole);
    const programNames = usersMemberships.map((m) => m.program?.name);
    const institutionNames = usersMemberships.map(
      (m) => m.program?.institution?.name
    );
    return {
      ...u,
      role: roles.join(",\r\n"),
      program: programNames.join(",\r\n"),
      institutionName: institutionNames.join(",\r\n"),
      status: u.eulaAcceptedDate ? "Active" : "Unregistered",
      programMemberships: {
        __typename: "ModelProgramMembershipConnection",
        items: usersMemberships
      }
    } as ProgramUser;
  });

  return finalResults;
};

export async function fetchUser(id: string) {
  return await fetchUserRest({ id });
}

export async function fetchUserStats(id: string) {
  const predicate = { userId: id };
  return await genericQuery<UserStatsQuery, UserStats>(
    queries.userStats,
    "userStats",
    predicate
  );
}

export type UpdateUserInput = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  npi?: number | null;
  pgy?: number | null;
  simplLearnId?: string | null;
  /** SE-3117: any identifier by key ("" clears); keys not sent are left untouched. */
  identifiers?: Record<string, string>;
  suffix?: string | null;
  jobTitle?: string | null;
  deviceToken?: string | null;
  devicePlatform?: string | null;
  cognitoUsername?: string | null;
};

export type UpdateUserResult = {
  success: boolean;
  updateUser: User;
  error?: string;
};

export async function updateUser(input: UpdateUserInput): Promise<User | null> {
  const path = "user";

  try {
    const response = await API.put("simpl-api", path, { body: input });

    // Response format: { success: true, updateUser: {...} } or { success: false, error: "..." }
    if (response?.success) {
      return response.updateUser as User;
    }

    const responseError = response?.error || "Failed to update user";
    throw new Error(responseError);
  } catch (caughtError: unknown) {
    throw new Error(apiErrorMessage(caughtError, "Failed to update user"));
  }
}

// The API's own error message, without the client's "HTTP 409: " prefix.
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  const e = error as { response?: any; message?: string };
  // SimplApiHttpError keeps the parsed body on `response`; Amplify used `response.data`.
  const body = e?.response?.data ?? e?.response;
  if (body && typeof body === "object") {
    if (typeof body.error === "string" && body.error) return body.error;
    if (typeof body.message === "string" && body.message) return body.message;
  }
  return e?.message || fallback;
};

export type CreateUserInput = {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  suffix?: string | null;
  npi?: number | null;
  pgy?: number | null;
  simplLearnId?: string | null;
  /** SE-3117: any identifier by key, e.g. { aamc_id: "12345678" }. */
  identifiers?: Record<string, string>;
  yearInStage?: number | null;
  simpl1Id?: number | null;
  jobTitle?: string | null;
  adminId?: string;
  programId: string;
  programRole: ProgramRole;
  traineeType?: string | null;
  startDate: string;
};

export type CreateUserResult = {
  success: boolean;
  userId?: string;
  programMembershipId?: string;
  error?: string;
  // Set when the error is about one identifier (e.g. a duplicate AAMC ID).
  identifierKey?: string;
};
export const createUser = async (
  input: CreateUserInput
): Promise<CreateUserResult> => {
  const path = "user";
  try {
    const response = (await API.post("simpl-api", path, {
      body: input
    })) as CreateUserResult;
    
    // Lambda returns { success: true, userId, programMembershipId } on success
    // or { success: false, error: string } on error
    if (response.success === false || response.error) {
      console.error("Error creating user:", response.error);
      // Return the error response so the caller can access the error message
      return {
        success: false,
        error: response.error || "Unknown error creating user",
        identifierKey: response.identifierKey
      };
    }
    
    return response;
  } catch (error: any) {
    // Handle API errors
    console.error("Error calling create-user lambda:", error);
    // Extract error message from response if available
    let errorMessage = "Unknown error creating user";
    // SimplApiHttpError keeps the parsed body on `response`; Amplify used `response.data`.
    const body = error?.response?.data ?? error?.response;
    if (body && typeof body === "object") {
      console.error("Lambda error response:", body);
      // Lambda returns { success: false, error: "..." } on error
      if (body.error) {
        errorMessage = body.error;
      } else if (body.message) {
        errorMessage = body.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }
    
    // Return error result instead of null so caller can access the error message
    return {
      success: false,
      error: errorMessage,
      identifierKey:
        typeof body?.identifierKey === "string" ? body.identifierKey : undefined
    };
  }
};

export const deleteUser = async (
  input: DeleteUserInput
): Promise<User | null> => {
  const predicate = {
    input: input
  };
  return await genericQuery<DeleteUserMutation, User>(
    mutations.deleteUser,
    "deleteUser",
    predicate
  );
};

export const updateUserEmail = async (
  input: UpdateUserEmailArgs
): Promise<string | null> => {
  const path = "user/email";
  const response = await API.put("simpl-api", path, {
    body: {
      userId: input.userId,
      newEmail: input.newEmail
    }
  });
  // Response format: { success: true, message: "..." } or { success: false, error: "..." }
  if (response.success) {
    return response.message;
  } else {
    throw new Error(response.error || "Failed to update user email");
  }
};

export const fetchSiblingUsers = async (
  userId: string,
  assessmentRole: AssessmentRole | null
) => {
  const args: PaginatableSiblingUsersArgs = {
    userId: userId,
    assessmentRole
  };

  const variables: PaginatableSiblingUsersQueryVariables = { input: args };
  return await listQueryAutoPaginated<
    PaginatableSiblingUsersQuery,
    User,
    PaginatableSiblingUsersResult
  >(
    customQueries.siblingUsersForCreate,
    "paginatableSiblingUsers",
    "items",
    variables
  );
};

export type ResendInvitationResult = {
  userId: string;
  success: boolean;
  error?: string;
  reasonCode?: string;
};

export type ResendInvitationSuccessResponse = {
  success: boolean;
  successCount: number;
  total: number;
  failed: number;
  results: ResendInvitationResult[];
};

export type ResendInvitationErrorResponse = {
  success: false;
  error: string;
};

export type ResendInvitationResponse =
  | ResendInvitationSuccessResponse
  | ResendInvitationErrorResponse;

export const sendUserInvitation = async (userId: string) => {
  const path = "invites/resend";
  try {
    const response = (await API.post("simpl-api", path, {
      body: { userIds: [userId] }
    })) as ResendInvitationResponse;

    if ("error" in response) {
      throw new Error(response.error);
    }

    // Check if the single user's invitation was successful
    const userResult = response.results.find((r) => r.userId === userId);
    if (!userResult || !userResult.success) {
      const errorMsg = userResult?.error || "Failed to send invitation";
      throw new Error(errorMsg);
    }

    // Return format compatible with old GraphQL response
    return {
      success: true,
      error: null
    } as SendUserInvitationResult;
  } catch (error: unknown) {
    console.error("Error calling resend-invitations lambda:", error);
    const errorMessage =
      error instanceof Error
        ? error.message
        : "Unknown error sending invitation";

    // Return format compatible with old GraphQL response
    return {
      success: false,
      error: errorMessage
    } as SendUserInvitationResult;
  }
};

export type AvailableParticipantsInput = {
  userId: string;
  assessmentRole: AssessmentRole;
  query?: string;
  sortOrder?: "asc" | "desc";
};
export type AvailableParticipant = {
  userId: string;
  fullName: string;
  initials: string;
  sharedProgramsDisplay: string;
  email: string;
};
export const availableParticipantsAPI = async ({
  userId,
  assessmentRole,
  query,
  sortOrder
}: AvailableParticipantsInput): Promise<AvailableParticipant[]> => {
  const availableParticipants = (await API.get(
    "simpl-api",
    "available-participants",
    {
      queryStringParameters: {
        userId: userId,
        assessmentRole: assessmentRole,
        query: query,
        sortOrder: sortOrder
      }
    }
  )) as AvailableParticipant[];
  return availableParticipants;
};
